import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import {
  ChevronRight, ChevronLeft, User, Calendar, Clock, Check,
  Search, MapPin, Star, Shield, AlertTriangle, Car, Heart,
  Plus, X, Loader2, CheckCircle, Users, ArrowRight, Info,
  AlertCircle, Sparkles
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

// Step indicator component
const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {steps.map((step, idx) => (
      <React.Fragment key={step.id}>
        <div className="flex items-center gap-2">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
              idx < currentStep
                ? "bg-[#06372B] text-white"
                : idx === currentStep
                ? "bg-[#06372B] text-white ring-4 ring-[#06372B]/20"
                : "bg-gray-100 text-gray-400"
            }`}
          >
            {idx < currentStep ? <Check className="w-4 h-4" /> : idx + 1}
          </div>
          <span
            className={`text-sm hidden sm:block ${
              idx <= currentStep ? "text-gray-900 font-medium" : "text-gray-400"
            }`}
          >
            {step.label}
          </span>
        </div>
        {idx < steps.length - 1 && (
          <div
            className={`w-12 h-0.5 ${
              idx < currentStep ? "bg-[#06372B]" : "bg-gray-200"
            }`}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Client card component
const ClientCard = ({ client, isSelected, onSelect }) => (
  <button
    onClick={() => onSelect(client)}
    data-testid={`client-card-${client.id}`}
    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
      isSelected
        ? "border-[#06372B] bg-[#06372B]/5"
        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
    }`}
  >
    <div className="flex items-start gap-4">
      {/* Avatar */}
      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#06372B]/10 to-[#06372B]/20 flex items-center justify-center flex-shrink-0">
        {client.photo_url ? (
          <img src={client.photo_url} alt="" className="w-14 h-14 rounded-full object-cover" />
        ) : (
          <span className="text-xl font-semibold text-[#06372B]">
            {client.first_name?.[0]}{client.last_name?.[0]}
          </span>
        )}
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-gray-900 truncate">{client.full_name}</h3>
          {isSelected && (
            <CheckCircle className="w-5 h-5 text-[#06372B] flex-shrink-0" />
          )}
        </div>
        
        {client.primary_diagnosis && (
          <p className="text-sm text-gray-500 mt-0.5">{client.primary_diagnosis}</p>
        )}
        
        {client.full_address && (
          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {client.city}, {client.state}
          </p>
        )}
        
        {/* Quick stats */}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {client.authorized_hours_weekly > 0 && (
            <Badge variant="outline" className="text-xs">
              {client.hours_remaining ?? client.authorized_hours_weekly}h remaining
            </Badge>
          )}
          {client.preferred_caregivers?.length > 0 && (
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-200 bg-amber-50">
              <Star className="w-3 h-3 mr-1" />
              {client.preferred_caregivers.length} preferred
            </Badge>
          )}
        </div>
      </div>
    </div>
  </button>
);

// Continuity Impact Badge
const ContinuityBadge = ({ level, label }) => {
  const styles = {
    excellent: { bg: "bg-emerald-100", text: "text-emerald-700", icon: "🟢" },
    good: { bg: "bg-blue-100", text: "text-blue-700", icon: "🟡" },
    acceptable: { bg: "bg-orange-100", text: "text-orange-700", icon: "🟠" },
    reduced: { bg: "bg-amber-100", text: "text-amber-700", icon: "🟠" },
    new: { bg: "bg-red-100", text: "text-red-700", icon: "🔴" },
  };
  
  const style = styles[level] || styles.new;
  
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${style.bg} ${style.text}`}>
      <span>{style.icon}</span>
      {label}
    </span>
  );
};

// Enhanced Caregiver recommendation card with Magic Moment UI
const CaregiverRecommendationCard = ({ caregiver, isSelected, onSelect }) => {
  const getScoreBg = (score) => {
    if (score >= 90) return "bg-emerald-500";
    if (score >= 75) return "bg-emerald-400";
    if (score >= 60) return "bg-blue-500";
    if (score >= 40) return "bg-amber-500";
    return "bg-red-400";
  };

  const getTierBadge = (tier) => {
    const styles = {
      primary: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Primary" },
      secondary: { bg: "bg-blue-100", text: "text-blue-700", label: "Secondary" },
      backup: { bg: "bg-orange-100", text: "text-orange-700", label: "Backup" },
    };
    return styles[tier];
  };

  const tierStyle = getTierBadge(caregiver.care_team_tier);

  return (
    <button
      onClick={() => onSelect(caregiver)}
      data-testid={`caregiver-card-${caregiver.caregiver_id}`}
      className={`w-full text-left rounded-xl border-2 transition-all overflow-hidden ${
        isSelected
          ? "border-[#06372B] bg-[#06372B]/5 shadow-lg"
          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left: Avatar + Name + Tier */}
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0 relative">
              {caregiver.photo_url ? (
                <img src={caregiver.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <User className="w-6 h-6 text-gray-500" />
              )}
              {isSelected && (
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#06372B] rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
            
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900">{caregiver.name}</h3>
                {tierStyle && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tierStyle.bg} ${tierStyle.text}`}>
                    {tierStyle.label}
                  </span>
                )}
              </div>
              
              {/* Continuity Impact */}
              <div className="mt-1">
                <ContinuityBadge level={caregiver.continuity_level} label={caregiver.continuity_label} />
              </div>
            </div>
          </div>
          
          {/* Right: Score */}
          <div className="flex flex-col items-end gap-1">
            <div className={`px-3 py-1.5 rounded-full text-white font-bold text-lg ${getScoreBg(caregiver.match_score)}`}>
              {Math.round(caregiver.match_score)}%
            </div>
            <span className="text-xs text-gray-400">Match</span>
          </div>
        </div>
      </div>
      
      {/* Why Recommended */}
      {caregiver.why_recommended?.length > 0 && (
        <div className="px-4 pb-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Why Recommended</p>
          <div className="flex flex-wrap gap-1.5">
            {caregiver.why_recommended.slice(0, 6).map((reason, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full"
              >
                <Check className="w-3 h-3" />
                {reason}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Care Plan Matches */}
      {caregiver.care_plan_matches?.length > 0 && (
        <div className="px-4 pb-3">
          <div className="flex flex-wrap gap-1.5">
            {caregiver.care_plan_matches.slice(0, 4).map((match, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full"
              >
                <Shield className="w-3 h-3" />
                {match}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Warnings */}
      {caregiver.warnings?.length > 0 && (
        <div className="px-4 pb-3">
          <div className="flex flex-wrap gap-1.5">
            {caregiver.warnings.map((warning, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-amber-50 text-amber-700 rounded-full"
              >
                <AlertTriangle className="w-3 h-3" />
                {warning}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Override Required */}
      {caregiver.requires_override && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 text-red-600 bg-red-50 p-2 rounded-lg">
            <AlertCircle className="w-4 h-4" />
            <span className="text-xs font-medium">Override Required</span>
          </div>
        </div>
      )}
      
      {/* Footer: Quick Stats */}
      <div className="flex items-center gap-4 px-4 py-3 bg-gray-50 border-t border-gray-100">
        {caregiver.distance_miles != null && (
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Car className="w-3 h-3" />
            {caregiver.distance_miles.toFixed(0)} mi
          </span>
        )}
        {caregiver.previous_visits_count > 0 && (
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Heart className="w-3 h-3" />
            {caregiver.previous_visits_count} visits
          </span>
        )}
        {caregiver.credentials_current && (
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Shield className="w-3 h-3" />
            Verified
          </span>
        )}
        {caregiver.overtime_risk !== "none" && (
          <span className="text-xs text-amber-600 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {caregiver.weekly_hours_after?.toFixed(1)}h/wk
          </span>
        )}
      </div>
    </button>
  );
};

// Main component
export default function SimplifiedVisitCreation() {
  const navigate = useNavigate();
  
  // Step state
  const [currentStep, setCurrentStep] = useState(0);
  const steps = [
    { id: "client", label: "Select Client" },
    { id: "caregiver", label: "Choose Caregiver" },
    { id: "schedule", label: "Schedule" },
    { id: "review", label: "Review" },
  ];
  
  // Data state
  const [searchQuery, setSearchQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  const [recommendations, setRecommendations] = useState([]);
  const [careTeamSummary, setCareTeamSummary] = useState(null);
  const [topRecommendation, setTopRecommendation] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  
  const [visitDate, setVisitDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("13:00");
  const [recurrenceType, setRecurrenceType] = useState("once");
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");
  
  const [validation, setValidation] = useState(null);
  const [loadingValidation, setLoadingValidation] = useState(false);
  
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState(null);
  
  // Set default date to today
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setVisitDate(today);
  }, []);
  
  // Search clients
  useEffect(() => {
    const searchClients = async () => {
      setLoadingClients(true);
      try {
        const { data } = await api.get(`/quick-visit/clients?q=${encodeURIComponent(searchQuery)}&limit=20`);
        setClients(data.clients);
      } catch (err) {
        console.error("Failed to load clients:", err);
      } finally {
        setLoadingClients(false);
      }
    };
    
    const debounce = setTimeout(searchClients, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);
  
  // Load Care Team recommendations when client + schedule is set
  const loadRecommendations = useCallback(async () => {
    if (!selectedClient || !visitDate || !startTime || !endTime) return;
    
    setLoadingRecs(true);
    try {
      // Use the new Care Team recommendations endpoint
      const { data } = await api.post("/quick-visit/care-team-recommendations", null, {
        params: {
          client_id: selectedClient.id,
          visit_date: visitDate,
          start_time: startTime,
          end_time: endTime,
        },
      });
      
      setRecommendations(data.recommendations || []);
      setCareTeamSummary(data.care_team_summary || null);
      setTopRecommendation(data.top_recommendation || null);
      
      // Auto-select top recommendation if available
      if (data.top_recommendation && !selectedCaregiver) {
        // Only auto-select if top rec doesn't require override
        if (!data.top_recommendation.requires_override) {
          setSelectedCaregiver(data.top_recommendation);
        }
      }
    } catch (err) {
      console.error("Failed to load Care Team recommendations:", err);
      // Fallback to original recommendations endpoint
      try {
        const { data } = await api.post("/quick-visit/recommendations", null, {
          params: {
            client_id: selectedClient.id,
            visit_date: visitDate,
            start_time: startTime,
            end_time: endTime,
          },
        });
        setRecommendations(data.recommendations || []);
      } catch (fallbackErr) {
        console.error("Fallback recommendations also failed:", fallbackErr);
        toast.error("Failed to load caregiver recommendations");
      }
    } finally {
      setLoadingRecs(false);
    }
  }, [selectedClient, visitDate, startTime, endTime, selectedCaregiver]);
  
  // Load recommendations when entering step 2
  useEffect(() => {
    if (currentStep === 1 && selectedClient) {
      loadRecommendations();
    }
  }, [currentStep, loadRecommendations, selectedClient]);
  
  // Validate schedule in real-time
  useEffect(() => {
    if (currentStep >= 2 && selectedClient && selectedCaregiver && visitDate && startTime && endTime) {
      const validate = async () => {
        setLoadingValidation(true);
        try {
          const { data } = await api.post("/quick-visit/validate-schedule", null, {
            params: {
              client_id: selectedClient.id,
              caregiver_id: selectedCaregiver.caregiver_id,
              visit_date: visitDate,
              start_time: startTime,
              end_time: endTime,
            },
          });
          setValidation(data);
        } catch (err) {
          console.error("Failed to validate:", err);
        } finally {
          setLoadingValidation(false);
        }
      };
      validate();
    }
  }, [currentStep, selectedClient, selectedCaregiver, visitDate, startTime, endTime]);
  
  // Handle client selection
  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setSelectedCaregiver(null); // Reset caregiver when client changes
    // Auto-advance after short delay
    setTimeout(() => setCurrentStep(1), 300);
  };
  
  // Handle caregiver selection
  const handleSelectCaregiver = (caregiver) => {
    setSelectedCaregiver(caregiver);
    setTimeout(() => setCurrentStep(2), 300);
  };
  
  // Handle create
  const handleCreate = async () => {
    if (!selectedClient || !selectedCaregiver || !visitDate) return;
    
    setCreating(true);
    try {
      const { data } = await api.post("/quick-visit/create", {
        client_id: selectedClient.id,
        caregiver_id: selectedCaregiver.caregiver_id,
        visit_date: visitDate,
        start_time: startTime,
        end_time: endTime,
        recurrence_type: recurrenceType,
        recurrence_end_date: recurrenceType !== "once" ? recurrenceEndDate : null,
      });
      
      setSuccess(data);
      toast.success("Visit created successfully!");
    } catch (err) {
      console.error("Failed to create visit:", err);
      toast.error(err.response?.data?.detail || "Failed to create visit");
    } finally {
      setCreating(false);
    }
  };
  
  // Calculate hours
  const calculateHours = () => {
    if (!startTime || !endTime) return 0;
    const [startH, startM] = startTime.split(":").map(Number);
    const [endH, endM] = endTime.split(":").map(Number);
    return Math.max(0, (endH + endM / 60) - (startH + startM / 60));
  };
  
  // Quick date buttons
  const today = new Date().toISOString().split("T")[0];
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
  
  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Visit Created!</h1>
          <p className="text-gray-500 mb-6">{success.message}</p>
          
          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Client</span>
              <span className="font-medium">{success.client_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Caregiver</span>
              <span className="font-medium">{success.caregiver_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Date</span>
              <span className="font-medium">{new Date(success.visit_date).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Time</span>
              <span className="font-medium">{success.start_time} - {success.end_time}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Hours</span>
              <span className="font-medium">{success.total_hours.toFixed(1)}h</span>
            </div>
          </div>
          
          <div className="text-xs text-gray-400 mb-6">
            <p className="font-medium mb-1">Workflows triggered:</p>
            <p>{success.workflows_triggered?.join(", ")}</p>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => navigate("/scheduling")}
            >
              View Calendar
            </Button>
            <Button
              className="flex-1 bg-[#06372B] hover:bg-[#0a4a3a]"
              onClick={() => {
                setSuccess(null);
                setCurrentStep(0);
                setSelectedClient(null);
                setSelectedCaregiver(null);
                setRecurrenceType("once");
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Another
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/scheduling")}
              className="p-2 hover:bg-gray-100 rounded-full"
              data-testid="close-visit-creation"
            >
              <X className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                Schedule Care
                <Sparkles className="w-5 h-5 text-amber-500" />
              </h1>
              <p className="text-sm text-gray-500">Intelligent scheduling powered by Care Team relationships</p>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Step indicator */}
        <StepIndicator currentStep={currentStep} steps={steps} />
        
        {/* Step content */}
        <AnimatePresence mode="wait">
          {/* Step 1: Select Client */}
          {currentStep === 0 && (
            <motion.div
              key="step-client"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Who needs care?</h2>
                <p className="text-gray-500 mt-1">Select a client to schedule a visit</p>
              </div>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 text-lg"
                  data-testid="client-search-input"
                  autoFocus
                />
              </div>
              
              {/* Client list */}
              <div className="space-y-3">
                {loadingClients ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#06372B]" />
                  </div>
                ) : clients.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No active clients found</p>
                  </div>
                ) : (
                  clients.map((client) => (
                    <ClientCard
                      key={client.id}
                      client={client}
                      isSelected={selectedClient?.id === client.id}
                      onSelect={handleSelectClient}
                    />
                  ))
                )}
              </div>
            </motion.div>
          )}
          
          {/* Step 2: Choose Caregiver — The Magic Moment */}
          {currentStep === 1 && (
            <motion.div
              key="step-caregiver"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-2">
                  <Sparkles className="w-6 h-6 text-amber-500" />
                  Who should provide care?
                </h2>
                <p className="text-gray-500 mt-1">
                  Caregivers ranked by Business Rules — deterministic, explainable, auditable
                </p>
              </div>
              
              {/* Selected client summary */}
              {selectedClient && (
                <div className="bg-white rounded-xl p-4 border border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#06372B]/10 flex items-center justify-center">
                      <span className="font-semibold text-[#06372B]">
                        {selectedClient.first_name?.[0]}{selectedClient.last_name?.[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{selectedClient.full_name}</p>
                      {(selectedClient.city || selectedClient.state) && (
                        <p className="text-sm text-gray-500">
                          {[selectedClient.city, selectedClient.state].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentStep(0)}
                    data-testid="change-client-btn"
                  >
                    Change
                  </Button>
                </div>
              )}
              
              {/* Care Team Summary */}
              {careTeamSummary && (
                <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium text-emerald-800">Care Team Status</span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className={`${careTeamSummary.has_primary ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {careTeamSummary.primary_available ? '🟢' : careTeamSummary.has_primary ? '🟡' : '⚪'} Primary
                    </span>
                    <span className={`${careTeamSummary.has_secondary ? 'text-blue-600' : 'text-gray-400'}`}>
                      {careTeamSummary.has_secondary ? '🟢' : '⚪'} Secondary
                    </span>
                    <span className={`${careTeamSummary.backup_count > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                      {careTeamSummary.backup_count > 0 ? '🟢' : '⚪'} {careTeamSummary.backup_count} Backup(s)
                    </span>
                  </div>
                </div>
              )}
              
              {/* Caregiver recommendations */}
              <div className="space-y-3">
                {loadingRecs ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#06372B] mb-3" />
                    <p className="text-gray-500">Finding best matches based on Care Team...</p>
                  </div>
                ) : recommendations.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No available caregivers found for this time</p>
                    <p className="text-sm mt-1">Try adjusting the schedule</p>
                  </div>
                ) : (
                  recommendations.map((caregiver) => (
                    <CaregiverRecommendationCard
                      key={caregiver.caregiver_id}
                      caregiver={caregiver}
                      isSelected={selectedCaregiver?.caregiver_id === caregiver.caregiver_id}
                      onSelect={handleSelectCaregiver}
                    />
                  ))
                )}
              </div>
              
              {/* Info callout */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Business Rules Scoring</p>
                    <p className="text-blue-600">
                      Care Team (30pts) + Skills (25pts) + Availability (20pts) + Compliance (15pts) + Distance (10pts) + Workload (10pts)
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setCurrentStep(0)} data-testid="back-to-client-btn">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </div>
            </motion.div>
          )}
          
          {/* Step 3: Schedule */}
          {currentStep === 2 && (
            <motion.div
              key="step-schedule"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">When?</h2>
                <p className="text-gray-500 mt-1">Choose the date and time for this visit</p>
              </div>
              
              {/* Summary cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Client</p>
                  <p className="font-medium">{selectedClient?.full_name}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Caregiver</p>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{selectedCaregiver?.name}</p>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      {Math.round(selectedCaregiver?.match_score || 0)}%
                    </Badge>
                  </div>
                  {selectedCaregiver?.continuity_label && (
                    <p className="text-xs text-gray-500 mt-1">{selectedCaregiver.continuity_label}</p>
                  )}
                </div>
              </div>
              
              {/* Date selection */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <div className="flex gap-2 mb-3">
                    <Button
                      variant={visitDate === today ? "default" : "outline"}
                      size="sm"
                      onClick={() => setVisitDate(today)}
                      className={visitDate === today ? "bg-[#06372B]" : ""}
                    >
                      Today
                    </Button>
                    <Button
                      variant={visitDate === tomorrow ? "default" : "outline"}
                      size="sm"
                      onClick={() => setVisitDate(tomorrow)}
                      className={visitDate === tomorrow ? "bg-[#06372B]" : ""}
                    >
                      Tomorrow
                    </Button>
                  </div>
                  <Input
                    type="date"
                    value={visitDate}
                    onChange={(e) => setVisitDate(e.target.value)}
                    min={today}
                    data-testid="visit-date-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                    <Input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      data-testid="start-time-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                    <Input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      data-testid="end-time-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Recurrence</label>
                  <Select value={recurrenceType} onValueChange={setRecurrenceType}>
                    <SelectTrigger data-testid="recurrence-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="once">One Time</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Biweekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {recurrenceType !== "once" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Until</label>
                    <Input
                      type="date"
                      value={recurrenceEndDate}
                      onChange={(e) => setRecurrenceEndDate(e.target.value)}
                      min={visitDate}
                    />
                  </div>
                )}
              </div>
              
              {/* Validation info */}
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Total Hours</p>
                    <p className="text-2xl font-bold text-gray-900">{calculateHours().toFixed(1)}h</p>
                  </div>
                  {selectedClient?.hours_remaining != null && (
                    <div>
                      <p className="text-gray-500">Authorization Remaining</p>
                      <p className="text-2xl font-bold text-gray-900">{selectedClient.hours_remaining}h</p>
                    </div>
                  )}
                </div>
                
                {/* Warnings */}
                {validation && (
                  <div className="mt-4 space-y-2">
                    {validation.travel_conflict && (
                      <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm">{validation.travel_conflict}</span>
                      </div>
                    )}
                    {validation.overtime_warning && (
                      <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm">{validation.overtime_warning}</span>
                      </div>
                    )}
                    {validation.will_exceed_authorization && (
                      <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm">Will exceed authorized hours</span>
                      </div>
                    )}
                    {!validation.travel_conflict && !validation.overtime_warning && !validation.will_exceed_authorization && (
                      <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-lg">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">No conflicts detected</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setCurrentStep(1)} data-testid="back-to-caregiver-btn">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setCurrentStep(3)}
                  className="bg-[#06372B] hover:bg-[#0a4a3a]"
                  disabled={!visitDate || !startTime || !endTime || validation?.travel_conflict}
                  data-testid="continue-to-review-btn"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}
          
          {/* Step 4: Review */}
          {currentStep === 3 && (
            <motion.div
              key="step-review"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Review & Create</h2>
                <p className="text-gray-500 mt-1">Confirm the details and create the visit</p>
              </div>
              
              {/* Summary */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                {/* Client */}
                <div className="p-6 border-b border-gray-100">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Client</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#06372B]/10 flex items-center justify-center">
                      <span className="font-semibold text-[#06372B]">
                        {selectedClient?.first_name?.[0]}{selectedClient?.last_name?.[0]}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-lg">{selectedClient?.full_name}</p>
                      <p className="text-sm text-gray-500">{selectedClient?.full_address}</p>
                    </div>
                  </div>
                </div>
                
                {/* Arrow */}
                <div className="flex justify-center -my-3 relative z-10">
                  <div className="w-8 h-8 bg-[#06372B] rounded-full flex items-center justify-center">
                    <ArrowRight className="w-4 h-4 text-white rotate-90" />
                  </div>
                </div>
                
                {/* Caregiver */}
                <div className="p-6 border-b border-gray-100">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Caregiver</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg">{selectedCaregiver?.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {selectedCaregiver?.care_team_tier && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                              {selectedCaregiver.care_team_tier.charAt(0).toUpperCase() + selectedCaregiver.care_team_tier.slice(1)} Caregiver
                            </Badge>
                          )}
                          <ContinuityBadge 
                            level={selectedCaregiver?.continuity_level || 'new'} 
                            label={selectedCaregiver?.continuity_label || 'New'} 
                          />
                        </div>
                      </div>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700 text-lg px-3 py-1">
                      {Math.round(selectedCaregiver?.match_score || 0)}% Match
                    </Badge>
                  </div>
                  
                  {/* Why this caregiver */}
                  {selectedCaregiver?.why_recommended?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Why Recommended</p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedCaregiver.why_recommended.slice(0, 4).map((reason, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full"
                          >
                            <Check className="w-3 h-3" />
                            {reason}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Arrow */}
                <div className="flex justify-center -my-3 relative z-10">
                  <div className="w-8 h-8 bg-[#06372B] rounded-full flex items-center justify-center">
                    <ArrowRight className="w-4 h-4 text-white rotate-90" />
                  </div>
                </div>
                
                {/* Schedule */}
                <div className="p-6">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Visit</p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-gray-900">
                        {new Date(visitDate).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                      </p>
                      <p className="text-sm text-gray-500">Date</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{startTime} - {endTime}</p>
                      <p className="text-sm text-gray-500">Time</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{calculateHours().toFixed(1)}h</p>
                      <p className="text-sm text-gray-500">Duration</p>
                    </div>
                  </div>
                  
                  {recurrenceType !== "once" && (
                    <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                      <Badge variant="outline" className="text-sm">
                        <Calendar className="w-3 h-3 mr-1" />
                        {recurrenceType === "weekly" ? "Weekly" : "Biweekly"} until {recurrenceEndDate || "ongoing"}
                      </Badge>
                    </div>
                  )}
                </div>
                
                {/* Stats */}
                {selectedClient?.hours_remaining != null && (
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Authorization Remaining After</span>
                      <span className="font-medium">
                        {(selectedClient.hours_remaining - calculateHours()).toFixed(1)}h
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Validation status */}
                <div className="px-6 py-4 border-t border-gray-200">
                  {validation?.is_valid !== false ? (
                    <div className="flex items-center gap-2 text-emerald-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Ready to Create</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-medium">{validation.travel_conflict || "Conflicts detected"}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setCurrentStep(2)} data-testid="back-to-schedule-btn">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating || validation?.is_valid === false}
                  className="bg-[#06372B] hover:bg-[#0a4a3a] px-8"
                  data-testid="create-visit-btn"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Create Visit
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
